import { useEffect, useRef } from "react";
import { trackBadgeGridImpression } from "@/lib/analytics";

/**
 * Fires a single `series_badge_impression` event the first time a badge grid
 * scrolls into view. Gives us the denominator for badge click-through rate.
 */
export function useBadgeGridImpression<T extends HTMLElement = HTMLElement>(
  placement: string,
  slugs: string[],
) {
  const ref = useRef<T | null>(null);
  const fired = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || fired.current) return;
    if (typeof IntersectionObserver === "undefined") {
      fired.current = true;
      trackBadgeGridImpression(placement, slugs);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !fired.current) {
            fired.current = true;
            trackBadgeGridImpression(placement, slugs);
            observer.disconnect();
          }
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
    // slugs is stable per placement in practice; join to avoid array identity churn
  }, [placement, slugs.join(",")]);

  return ref;
}

/** One-shot hover/focus tracker per badge tile. */
export function useOnceFlag() {
  const seen = useRef<Set<string>>(new Set());
  return (key: string) => {
    if (seen.current.has(key)) return false;
    seen.current.add(key);
    return true;
  };
}
